import React, { FunctionComponent, memo, PropsWithChildren, ReactNode, useCallback } from 'react';
import './DataCardRow.scss';
import { DataCradRowContent } from './components';
import { DEFAULT_NUMBER_PRECISION, formatSiNumber } from '@utils';
import { DataRowSeverity } from './components/utils';

export interface DataCardRowProps<T> {
	title: string;
	value: string | ReactNode;
	isEditable: boolean;
	totalValue?: boolean | number | string | null;
	customValue?: boolean | number | string | null;
	editPermission?: boolean;
	suffix?: string;
	logAmount?: boolean | number | string | null;
	modal?: ReactNode;
	openModal?: () => void;
	additionalClassName?: string | null;
	omitLimitNumbersFormatting?: boolean | null;
	severity?: DataRowSeverity;
}

export const DataCardRow = <T extends unknown>({
	title,
	value,
	totalValue,
	customValue,
	suffix,
	isEditable,
	editPermission,
	modal,
	logAmount,
	openModal,
	additionalClassName,
	omitLimitNumbersFormatting,
	severity
}: PropsWithChildren<DataCardRowProps<T>>) => {
	const hasModal = isEditable && editPermission;

	const formattedValue = useCallback(
		(value: number | string | boolean | null | undefined, omitLimitNumbersFormatting: boolean | null | undefined) =>
			{ 
				if (typeof value === 'number' && 
					!(typeof omitLimitNumbersFormatting === 'boolean' && omitLimitNumbersFormatting))
				{
					return value ? 
						formatSiNumber(value, {
								precision: DEFAULT_NUMBER_PRECISION,
							})  : value;
				}
				return value;	
			},
		[],
	);

	return (
		<>
			<DataCradRowContent
				title={title}
				value={value}
				totalValue= {formattedValue(totalValue, omitLimitNumbersFormatting)}
				customValue= {formattedValue(customValue, omitLimitNumbersFormatting)}
				suffix={suffix}
				isEditable={isEditable}
				editPermission={editPermission ?? false}
				openModal={openModal}
				logAmount={logAmount}
				additionalClassName={additionalClassName}
				severity={severity ?? DataRowSeverity.DEFAULT}
			/>
			{hasModal && modal}
		</>
	);
};

export function createDataCardRowComponent<T>(): FunctionComponent<DataCardRowProps<T>> {
	return (memo(DataCardRow) as unknown) as FunctionComponent<DataCardRowProps<T>>;
}
